import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { updateFeedbackStatus, updateFeedbackPriority } from '../../firebase/services';
import { BottomNav } from '../common/BottomNav';
import * as XLSX from 'xlsx';
import './AdminFeedback.css';

const AdminFeedback = () => {
  const navigate = useNavigate();
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'new', 'in-progress', 'resolved', 'wont-fix'

  useEffect(() => {
    // Set up real-time listener
    const q = query(
      collection(db, 'feedback'),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const feedback = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setFeedbackList(feedback);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching feedback:', err);
        setError('Failed to load feedback');
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, []);

  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      await updateFeedbackStatus(feedbackId, newStatus);
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  const handlePriorityChange = async (feedbackId, newPriority) => {
    try {
      await updateFeedbackPriority(feedbackId, newPriority);
    } catch (err) {
      console.error('Error updating priority:', err);
      alert('Failed to update priority');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return '#007bff';
      case 'in-progress':
        return '#ffc107';
      case 'resolved':
        return '#28a745';
      case 'wont-fix':
        return '#6c757d';
      default:
        return '#333';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#dc3545'; // Red
      case 'medium':
        return '#ffc107'; // Yellow
      case 'low':
        return '#28a745'; // Green
      default:
        return '#6c757d'; // Gray for no priority
    }
  };

  // Sort feedback: resolved/wont-fix at bottom, newest first within each group
  const getSortedFeedback = () => {
    const sorted = [...feedbackList].sort((a, b) => {
      // Priority order: new (or no status) -> in-progress -> wont-fix -> resolved
      const statusOrder = { 'new': 1, 'in-progress': 2, 'wont-fix': 3, 'resolved': 4 };
      const aOrder = statusOrder[a.status] || 1; // Treat missing status as 'new'
      const bOrder = statusOrder[b.status] || 1;

      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      // Within same status, sort by timestamp (newest first)
      const aTime = a.timestamp?.toDate?.() || new Date(0);
      const bTime = b.timestamp?.toDate?.() || new Date(0);
      return bTime - aTime;
    });

    return sorted;
  };

  // Filter feedback by status
  const getFilteredFeedback = () => {
    const sorted = getSortedFeedback();
    if (filterStatus === 'all') {
      return sorted;
    }
    if (filterStatus === 'new') {
      return sorted.filter(f => !f.status || f.status === 'new');
    }
    return sorted.filter(f => f.status === filterStatus);
  };

  const filteredFeedback = getFilteredFeedback();

  // Count by status for filter buttons
  const statusCounts = {
    all: feedbackList.length,
    new: feedbackList.filter(f => !f.status || f.status === 'new').length,
    'in-progress': feedbackList.filter(f => f.status === 'in-progress').length,
    resolved: feedbackList.filter(f => f.status === 'resolved').length,
    'wont-fix': feedbackList.filter(f => f.status === 'wont-fix').length,
  };

  // Export functions
  const formatDateForExport = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const prepareExportData = () => {
    return filteredFeedback.map(feedback => ({
      'Date': formatDateForExport(feedback.timestamp),
      'Category': feedback.category || 'N/A',
      'Feedback': feedback.feedbackText || '',
      'User Email': feedback.userEmail || 'Anonymous',
      'Status': feedback.status || 'new',
      'Priority': feedback.priority || 'none',
      'Device': feedback.device || 'N/A',
      'App Version': feedback.appVersion || 'N/A',
      'Resolved Date': feedback.resolvedDate ? formatDateForExport(feedback.resolvedDate) : 'N/A'
    }));
  };

  const exportToCSV = () => {
    const data = prepareExportData();

    if (data.length === 0) {
      alert('No feedback to export');
      return;
    }

    // Create CSV content
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row =>
        headers.map(header => {
          const value = row[header];
          // Escape quotes and wrap in quotes if contains comma or newline
          if (typeof value === 'string' && (value.includes(',') || value.includes('\n') || value.includes('"'))) {
            return '"' + value.replace(/"/g, '""') + '"';
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `lightway-feedback-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToXLSX = () => {
    const data = prepareExportData();

    if (data.length === 0) {
      alert('No feedback to export');
      return;
    }

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(data);

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Feedback');

    // Generate filename with current date
    const filename = `lightway-feedback-${new Date().toISOString().split('T')[0]}.xlsx`;

    // Download file
    XLSX.writeFile(wb, filename);
  };

  if (loading) {
    return <div className="admin-loading">Loading feedback...</div>;
  }

  if (error) {
    return <div className="admin-error">{error}</div>;
  }

  return (
    <div className="admin-feedback">
      <header className="admin-header">
        <button className="back-button" onClick={() => navigate('/home')}>
          ←
        </button>
        <div className="header-content">
          <h1>Feedback Management</h1>
          <p className="admin-subtitle">Total Feedback: {feedbackList.length}</p>
          <button
            onClick={() => navigate('/admin/deletions')}
            style={{
              background: 'none',
              border: '1px solid #ddd',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '0.85rem',
              color: '#333',
              cursor: 'pointer',
              marginTop: '8px',
            }}
          >
            Account Deletions →
          </button>
        </div>
      </header>

      <div className="admin-controls">
        <div className="controls-row">
          <div className="view-toggle">
            <button
              className={`toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
              onClick={() => setViewMode('card')}
            >
              Card View
            </button>
            <button
              className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              Table View
            </button>
          </div>

          <div className="export-buttons">
            <button className="export-btn" onClick={exportToCSV} title="Export to CSV">
              <span>📄</span> Export CSV
            </button>
            <button className="export-btn" onClick={exportToXLSX} title="Export to Excel">
              <span>📊</span> Export XLSX
            </button>
          </div>
        </div>

        <div className="status-filters">
          <button
            className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            All ({statusCounts.all})
          </button>
          <button
            className={`filter-btn ${filterStatus === 'new' ? 'active' : ''}`}
            onClick={() => setFilterStatus('new')}
          >
            New ({statusCounts.new})
          </button>
          <button
            className={`filter-btn ${filterStatus === 'in-progress' ? 'active' : ''}`}
            onClick={() => setFilterStatus('in-progress')}
          >
            In Progress ({statusCounts['in-progress']})
          </button>
          <button
            className={`filter-btn ${filterStatus === 'resolved' ? 'active' : ''}`}
            onClick={() => setFilterStatus('resolved')}
          >
            Resolved ({statusCounts.resolved})
          </button>
          <button
            className={`filter-btn ${filterStatus === 'wont-fix' ? 'active' : ''}`}
            onClick={() => setFilterStatus('wont-fix')}
          >
            Won't Fix ({statusCounts['wont-fix']})
          </button>
        </div>
      </div>

      <main className="admin-content">
        {filteredFeedback.length === 0 ? (
          <div className="no-feedback">
            {feedbackList.length === 0 ? 'No feedback submitted yet.' : 'No feedback matches the selected filter.'}
          </div>
        ) : viewMode === 'card' ? (
          <div className="feedback-grid">
            {filteredFeedback.map((feedback) => (
              <div key={feedback.id} className="feedback-card">
                <div className="feedback-header">
                  <span
                    className="feedback-category"
                    style={{ backgroundColor: getStatusColor(feedback.status), color: 'white' }}
                  >
                    {feedback.category}
                  </span>
                  <span className="feedback-date">
                    {formatDate(feedback.timestamp)}
                  </span>
                </div>

                <div className="feedback-body">
                  <p className="feedback-text">{feedback.feedbackText}</p>
                </div>

                <div className="feedback-meta">
                  <div className="meta-item">
                    <strong>User:</strong> {feedback.userEmail || 'Anonymous'}
                  </div>
                  <div className="meta-item">
                    <strong>Device:</strong> {feedback.device ? feedback.device.substring(0, 50) + '...' : 'N/A'}
                  </div>
                  <div className="meta-item">
                    <strong>Version:</strong> {feedback.appVersion || 'N/A'}
                  </div>
                </div>

                <div className="feedback-actions">
                  <label htmlFor={`status-${feedback.id}`}>Status:</label>
                  <select
                    id={`status-${feedback.id}`}
                    value={feedback.status || 'new'}
                    onChange={(e) => handleStatusChange(feedback.id, e.target.value)}
                    className="status-select"
                  >
                    <option value="new">New</option>
                    <option value="in-progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="wont-fix">Won't Fix</option>
                  </select>
                </div>

                <div className="feedback-actions">
                  <label htmlFor={`priority-${feedback.id}`}>Priority:</label>
                  <select
                    id={`priority-${feedback.id}`}
                    value={feedback.priority || ''}
                    onChange={(e) => handlePriorityChange(feedback.id, e.target.value)}
                    className="priority-select"
                    style={{ borderColor: getPriorityColor(feedback.priority) }}
                  >
                    <option value="">None</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                  {feedback.priority && (
                    <span
                      className="priority-indicator"
                      style={{ backgroundColor: getPriorityColor(feedback.priority) }}
                    >
                      {feedback.priority.toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="feedback-table-container">
            <table className="feedback-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Feedback</th>
                  <th>User</th>
                  <th>Priority</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredFeedback.map((feedback) => (
                  <tr key={feedback.id}>
                    <td className="date-cell">{formatDate(feedback.timestamp)}</td>
                    <td>
                      <span
                        className="table-category-badge"
                        style={{ backgroundColor: getStatusColor(feedback.status) }}
                      >
                        {feedback.category}
                      </span>
                    </td>
                    <td className="feedback-cell">
                      <div className="feedback-text-preview">{feedback.feedbackText}</div>
                      <div className="feedback-details">
                        <small>Device: {feedback.device ? feedback.device.substring(0, 30) + '...' : 'N/A'}</small>
                        <small>Version: {feedback.appVersion || 'N/A'}</small>
                      </div>
                    </td>
                    <td className="user-cell">{feedback.userEmail || 'Anonymous'}</td>
                    <td className="priority-cell">
                      <select
                        value={feedback.priority || ''}
                        onChange={(e) => handlePriorityChange(feedback.id, e.target.value)}
                        className="table-priority-select"
                        style={{
                          borderColor: getPriorityColor(feedback.priority),
                          backgroundColor: feedback.priority ? getPriorityColor(feedback.priority) + '20' : 'white'
                        }}
                      >
                        <option value="">None</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </td>
                    <td className="status-cell">
                      <select
                        value={feedback.status || 'new'}
                        onChange={(e) => handleStatusChange(feedback.id, e.target.value)}
                        className="table-status-select"
                      >
                        <option value="new">New</option>
                        <option value="in-progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="wont-fix">Won't Fix</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
};

export default AdminFeedback;
